import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "@/pages/category/components/FilterBar";
import { genreOptions, mediaTypeLabels, regionOptions, yearOptions } from "@/mocks/movies";
import type { MediaType } from "@/mocks/movies";
import { filterMovies, sortMovies } from "@/utils/movie";
import type { SortKey } from "@/utils/movie";
import { movies } from "@/mocks/movies";

const PAGE_SIZE = 10;

const tabs: { key: MediaType | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "updated", label: "按更新时间", icon: "ri-time-line" },
  { key: "hot", label: "按热度", icon: "ri-fire-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
];

function resolveType(value: string | null): MediaType | "all" {
  if (value === "movie" || value === "tv" || value === "variety") return value;
  return "all";
}

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = resolveType(searchParams.get("type"));

  const [genre, setGenre] = useState("all");
  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [sort, setSort] = useState<SortKey>("updated");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, genre, region, year, sort]);

  const result = useMemo(() => {
    const filtered = filterMovies(movies, { type, genre, region, year });
    return sortMovies(filtered, sort);
  }, [type, genre, region, year, sort]);

  const shown = result.slice(0, visible);
  const activeTabLabel = type === "all" ? "全部影视" : mediaTypeLabels[type];
  const hasFilter = genre !== "all" || region !== "all" || year !== "all";

  const resetFilters = () => {
    setGenre("all");
    setRegion("all");
    setYear("all");
  };

  const selectTab = (key: MediaType | "all") => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") {
      next.delete("type");
    } else {
      next.set("type", key);
    }
    setSearchParams(next);
  };

  return (
    <div className="mx-auto max-w-page px-4 md:px-8 pt-8 md:pt-12">
      <Breadcrumb items={[{ label: "影视分类" }]} />

      <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
            {activeTabLabel} · 午夜影视分类
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
            在这里按类型、地区、年份自由组合筛选，快速定位你想看的午夜影视作品。每一条资源都配有独立详情页，信息与列表保持一致。
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSort(option.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs md:text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sort === option.key
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-200/70 text-foreground-700 hover:bg-background-300/70"
              }`}
            >
              <i className={option.icon}></i>
              <span className="hidden sm:inline">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-300/60 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => selectTab(tab.key)}
            className={`px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              type === tab.key
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <FilterBar
        genres={genreOptions}
        regions={regionOptions}
        years={yearOptions}
        genre={genre}
        region={region}
        year={year}
        onGenre={setGenre}
        onRegion={setRegion}
        onYear={setYear}
      />

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <span className="text-primary-400 font-semibold">{result.length}</span> 部影视资源
        </p>
        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        )}
      </div>

      {result.length === 0 ? (
        <div className="mt-6 rounded-lg bg-background-100 border border-background-300/60 py-16 px-6 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-background-200/80 flex items-center justify-center text-foreground-600">
            <i className="ri-movie-2-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading font-bold text-lg text-foreground-950">没有符合条件的影视</h3>
          <p className="mt-2 text-sm text-foreground-600">当前筛选组合下暂无内容，试试放宽条件或重置筛选。</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选条件
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {shown.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} index={index} />
            ))}
          </div>
          {visible < result.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-200/80 border border-background-300/70 text-foreground-950 text-sm font-medium hover:border-primary-500/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line text-lg"></i>
                加载更多（剩余 {result.length - visible}）
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}